import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import { Sparkles, X, CheckCircle2, Loader2 } from 'lucide-react'
import { useIdioma } from '../context/IdiomaContext'
import billingService from '../services/billing.service'

export default function UpgradeModal({ isOpen, onClose, featureName }) {
  useIdioma()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const startCheckout = async () => {
    try {
      setLoading(true)
      setError('')
      const { checkoutUrl } = await billingService.createCheckout()
      window.location.assign(checkoutUrl)
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'No pudimos iniciar el pago. Intentá nuevamente.')
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="w-full max-w-md bg-white dark:bg-gray-900 border border-indigo-100 dark:border-indigo-500/20 rounded-[2rem] shadow-2xl relative overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Background gradient effects */}
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-indigo-500/20 blur-[50px] rounded-full pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-purple-500/20 blur-[50px] rounded-full pointer-events-none" />

          {/* Close Button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 bg-gray-100/50 dark:bg-gray-800/50 rounded-full transition-colors z-50 cursor-pointer"
          >
            <X size={20} />
          </button>

          <div className="p-8 relative z-10">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-indigo-500/30">
              <Sparkles className="text-white" size={32} />
            </div>

            <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight mb-2">
              Desbloqueá el poder de DayFlow
            </h2>
            
            <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed mb-6">
              Estás intentando acceder a <strong className="text-indigo-600 dark:text-indigo-400">{featureName}</strong> o llegaste a tu límite del plan gratuito. Actualizá a Premium para desbloquear todo.
            </p>

            <div className="space-y-3 mb-8">
              {[
                'Workspaces ilimitados',
                'Hábitos, Tareas y Notas infinitos',
                'Análisis Inteligente con IA (Calorías)',
                'Estadísticas Avanzadas de Productividad',
                'Soporte Prioritario'
              ].map((benefit, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{benefit}</span>
                </div>
              ))}
            </div>

            <button 
              onClick={startCheckout}
              disabled={loading}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold text-sm tracking-wide uppercase transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:shadow-md"
            >
              {loading ? <Loader2 className="mx-auto animate-spin" size={20} /> : 'Actualizar a Premium'}
            </button>
            {error && <p className="text-center text-sm text-red-500 mt-3">{error}</p>}
            <p className="text-center text-[10px] text-gray-400 mt-4 uppercase tracking-widest font-bold">
              Cancela cuando quieras
            </p>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}
